/* eslint-disable */
import * as BABYLON from 'babylonjs';
import CoreUtils from '../utils/coreUtils.js';
import GeoUtils, { UV_SCALE_MULT } from './nodeGraph/geoUtils.js';
import Utils2D from '../utils/utils2D.js';
import Utils3D from './utils/utils3D.js';
import Spline from './nodeGraph/spline.js';

const BEVEL_INTERP_FUNCTIONS = {
    'linear': CoreUtils.interpolate,
    'round': CoreUtils.interpolateCircular,
};

const DEBUG_VISUALIZE_AXES = false;
const DEBUG_PRINT_AXES_INFO = false;
const DEBUG_AXES_SIZE = 0.05;

export default class Extrude
{
/*****************************************************************************/
static linearScaleInterp = (fDistanceInterp, fStartScale, fEndScale) =>
{
    let fValue = CoreUtils.interpolate(fDistanceInterp, fStartScale, fEndScale);
    return CoreUtils.clamp(fValue, 1e-5, Number.MAX_VALUE);
};
/*****************************************************************************/
static bevelScaleInterp = (fDistanceInterp, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp) =>
{
    if(!funcStartBevelInterp)
        funcStartBevelInterp = CoreUtils.interpolate;
    if(!funcEndBevelInterp)
        funcEndBevelInterp = CoreUtils.interpolate;

    let fValue;
    if(fDistanceInterp < fStartBevelWidth || fStartBevelWidth >= 1.0)
        fValue = funcStartBevelInterp(1.0 - fDistanceInterp/fStartBevelWidth, fStartScale, fStartBevelScale);
    else if(fDistanceInterp > 1.0 - fEndBevelWidth || fEndBevelWidth >= 1.0)
        fValue = funcEndBevelInterp((fDistanceInterp - 1.0 + fEndBevelWidth)/fEndBevelWidth, fEndScale, fEndBevelScale);
    else
        fValue = CoreUtils.interpolate((fDistanceInterp - fStartBevelWidth)/(1.0 - fStartBevelWidth - fEndBevelWidth), fStartScale, fEndScale);
    return CoreUtils.clamp(fValue, 1e-5, Number.MAX_VALUE);
};
/*****************************************************************************/
static simpleScale = (vecPointIn, fRelScale, shape, iShapeVertex, iPathVertex, axisX, axisY, fCumulOffset, vecScaledPointOut) =>
{
	vecPointIn.scaleToRef(fRelScale, vecScaledPointOut);
    return fRelScale;
};
/*****************************************************************************/
static smartScale = (vecPointIn, fRelScale, shape, iShapeVertex, iPathVertex, axisX, axisY, fCumulOffset, vecScaledPointOut) =>
{
    // Build a spline object from shape points:
    let pSpline = new Spline(shape, true);

    pSpline.offsetCurve(fCumulOffset);

    let vecSize = pSpline.getSize();
    let fOffsetReferenceDim = Math.max(vecSize.x, vecSize.y)/2.0;

    let iNumNewPoints = pSpline.getNumPoints();
    let vecOrigPoint = pSpline.getPoint(iShapeVertex % iNumNewPoints).point;

    let fNewOffset = fOffsetReferenceDim*(fRelScale - 1.0);
    pSpline.offsetCurve(fNewOffset);    

    // Get the new point at the same index:
    iNumNewPoints = pSpline.getNumPoints();
    let vecScaledPoint = pSpline.getPoint(iShapeVertex % iNumNewPoints).point;

    let vecDelta = vecScaledPoint.subtract(vecOrigPoint);

    let vecNewPos = vecPointIn.add(axisX.scale(vecDelta.x)).add(axisY.scale(vecDelta.y));
    vecScaledPointOut.copyFrom(vecNewPos);

    return fNewOffset;
};
/*****************************************************************************/
static unprojectPoint(strPointsOrient, point2D)
{
    let point3D = BABYLON.Vector3.Zero();
    if(strPointsOrient === 'xy')
    {
        point3D.x = point2D.x;
        point3D.y = point2D.y;        
    }
    else if(strPointsOrient === 'xz')
    {
        point3D.x = point2D.x;
        point3D.z = point2D.y;
    }
    else if(strPointsOrient === 'zy')
    {
        point3D.z = point2D.x;
        point3D.z = point2D.y;
    }

    return point3D;
}
/*****************************************************************************/
static createMesh(name, options, scene)
{
    let shape = options.shape;
    let pPathSpline = options.path;

    let capType = options.cap;
	const bDoClose = (options.close === undefined) ? pPathSpline.getIsClosed() : options.close;
	// Don't cap the ends if the path is closed since they won't be
	// visible anyway
	if(bDoClose)
		capType = BABYLON.Mesh.NO_CAP;

	// Generate correct up vector:
    const strPointsOrient = options.extrusionPlane ? options.extrusionPlane : 'xy';
	let vecUp;
    if(options.up)
        vecUp = options.up;
	else if(strPointsOrient == 'zy')
		vecUp = new BABYLON.Vector3(1, 0, 0);
	else if(strPointsOrient == 'xy')
		vecUp = new BABYLON.Vector3(0, 0, 1);
	else if(strPointsOrient == 'xz')
		vecUp = new BABYLON.Vector3(0, 1, 0);

    const wasPath3D = pPathSpline.getIs3D();
    if(!wasPath3D)
    {
        let rawPathPoints = pPathSpline.toPointList();
        for(let pointInfo of rawPathPoints)
            pointInfo.point = Extrude.unprojectPoint(strPointsOrient, pointInfo.point);
    }

    const origPathCenter = pPathSpline.getCenter();

    // We need to ensure a consistent orientation for paths, but only if they're closed:
    // Otherwise, we break open paths that are meant to start at the shape, like for door
    // frames.
    if(pPathSpline.getIsClosed())
        pPathSpline.ensureOrientation();

    let path = pPathSpline.getLinearPoints3D(false);

    let iNumPathPoints = path.length;
	if(iNumPathPoints < 2 || shape.length <= 2)
		return null;

    let line = BABYLON.Vector3.Zero();
    let nextLine = BABYLON.Vector3.Zero();
    let axisX = BABYLON.Vector3.Zero();
    let axisY = BABYLON.Vector3.Zero();
    let axisZ = BABYLON.Vector3.Zero();
    let nextAxisX = BABYLON.Vector3.Zero();
    let nextAxisY = BABYLON.Vector3.Zero();
    let nextAxisZ = BABYLON.Vector3.Zero();
    let startPoint = BABYLON.Vector3.Zero();
    let nextStartPoint = BABYLON.Vector3.Zero();
    let tempCurrDiff = BABYLON.Vector3.Zero();
    let bisector = BABYLON.Vector3.Zero();
    let distance = 0;
    let ray;
    let allPaths = [];

    let fStartScale = options.startScale !== undefined ? options.startScale : 1.0;
    let fEndScale = options.endScale !== undefined ? options.endScale : 1.0;

    let fStartBevelWidth = options.startBevelWidth !== undefined ? options.startBevelWidth : 0.0;
    let fStartBevelScale = options.startBevelScale !== undefined ? options.startBevelScale : 0.0;
    let fEndBevelWidth = options.endBevelWidth !== undefined ? options.endBevelWidth : 0.0;
    let fEndBevelScale = options.endBevelScale !== undefined ? options.endBevelScale : 0.0;

    const bHavePerStepScale = fStartScale != 1.0 || fEndScale != 1.0 || fStartBevelWidth != 0.0 || fEndBevelWidth != 0.0 || options.funcPerStepShapePointOverride;

    let plane;
    let planeNormal;
    let planeParallel;

    let fSignedArea = GeoUtils.calculateSignedPolygonAreaXY(shape);
    if(fSignedArea > 0)
        shape.reverse();

    // We need to align the shape to the path, otherwise its location affects the final mesh shape.
    let pPathCumulDistances = [];
    let fTotalPathLength = 0;

    let shapeAlignOffset = new BABYLON.Vector2.Zero();

    let pBBox = Utils2D.computePointsBBox(shape);

    let vecShapeSize = pBBox.max.subtract(pBBox.min);
    let vecShapeCenter = pBBox.max.add(pBBox.min).scale(0.5);
    // We always treat the shape center as if its in the XZ plane
    let vecShapeCenter3D = Extrude.unprojectPoint('xz', vecShapeCenter);

    const yAlignOffset = options.shapeAlignYOffset ? options.shapeAlignYOffset : 0;
    if(options.shapeAlignY == 'inside')
        shapeAlignOffset.y = vecShapeSize.y/2.0 + yAlignOffset;
    else if(options.shapeAlignY == 'outside')
        shapeAlignOffset.y = -vecShapeSize.y/2.0 - yAlignOffset;
    else
        shapeAlignOffset.y -= yAlignOffset;

    const xAlignOffset = options.shapeAlignXOffset ? options.shapeAlignXOffset : 0;
    if(options.shapeAlignX == 'above')
        shapeAlignOffset.x = vecShapeSize.x/2.0 + xAlignOffset;
    else if(options.shapeAlignX == 'below')
        shapeAlignOffset.x = -vecShapeSize.x/2.0 - xAlignOffset;
    else
        shapeAlignOffset.x += xAlignOffset;
    
    // Center shape to (0,0) in 2D
    // Put the path so that its first vertex is also at 0,0
    let vecPathStartPoint = path[0].clone();
    path.forEach((point) => { 
        point.x -= vecPathStartPoint.x;
        point.y -= vecPathStartPoint.y;
        point.z -= vecPathStartPoint.z;
    });
    shape.forEach((point) => { 
        point.x -= vecShapeCenter.x;
        point.y -= vecShapeCenter.y;
    });

    // Align the shape relative to the path
    shape.forEach((point) => { 
        point.x += shapeAlignOffset.x;
        point.y += shapeAlignOffset.y;
    });

    const vecInitDir = path[1].subtract(path[0]);
    vecInitDir.normalize();

    let vecLastDir = path[0].subtract(path[path.length - 1]);
    vecLastDir.normalize();
    if(!bDoClose)
        vecLastDir = vecInitDir;

    pPathCumulDistances.push(0);
    for(let iPathVertex = 1; iPathVertex < iNumPathPoints; iPathVertex++)
    {
        path[iPathVertex].subtractToRef(path[iPathVertex - 1], line);
        fTotalPathLength += line.length();
        pPathCumulDistances.push(fTotalPathLength);
    }

    let funcScaleInterp = Extrude.linearScaleInterp;
    if(fStartBevelWidth > 0.0 || fEndBevelWidth > 0.0)
        funcScaleInterp = Extrude.bevelScaleInterp;

    let funcStartBevelInterp = options.startBevelShape ? BEVEL_INTERP_FUNCTIONS[options.startBevelShape] : BEVEL_INTERP_FUNCTIONS['linear'];
    let funcEndBevelInterp = options.endBevelShape ? BEVEL_INTERP_FUNCTIONS[options.endBevelShape] : BEVEL_INTERP_FUNCTIONS['linear'];

    let funcScaleShapePoint = options.scalingMethod ? SCALING_FUNCTIONS[options.scalingMethod] : Extrude.simpleScale;
    let funcScaleShapePointBevelStart = options.startBevelScalingMethod ? SCALING_FUNCTIONS[options.startBevelScalingMethod] : Extrude.simpleScale;
    let funcScaleShapePointBevelEnd = options.endBevelScalingMethod ? SCALING_FUNCTIONS[options.endBevelScalingMethod] : Extrude.simpleScale;

    let funcGetScalingFunction = (fDistanceInterp, fStartBevelAmount, fEndBevelAmount) => {

        if(options.funcPerStepShapePointOverride)
            return options.funcPerStepShapePointOverride;

        if(fDistanceInterp < fStartBevelAmount)
            return funcScaleShapePointBevelStart;
        else if(fDistanceInterp > 1.0 - fEndBevelAmount)
            return funcScaleShapePointBevelEnd;
        else
            return funcScaleShapePoint;
    };

    let funcScalingFunction;

    let debugXAxes = [];
    let debugYAxes = [];
    let debugZAxes = [];
    let debugPlaneNormals = [];

    // Go over every shape vertex and create a path for each one along the length of
    // the extrusion specified by the path curve
    let vecShapeAlignOffset3D = null;

    let nextUnscaled = BABYLON.Vector3.Zero();
    let scaledShapePoint = BABYLON.Vector3.Zero();
    let fPrevScale = 1.0;
    for(let iShapeVertex = 0; iShapeVertex < shape.length; iShapeVertex++)
    {
        let cumulScaledOffset = 0.0;

        axisZ = vecInitDir.clone();
        axisX = vecUp;
        axisY = BABYLON.Vector3.Cross(axisZ, axisX);

        if(!vecShapeAlignOffset3D)
            vecShapeAlignOffset3D = axisX.scale(shapeAlignOffset.x);

        // Go over every path vertex and compute the path of the current shape vertex
        startPoint = path[0].add(axisX.scale(shape[iShapeVertex].x)).add(axisY.scale(shape[iShapeVertex].y));
        if(bHavePerStepScale)
		{
            startPoint.subtractToRef(path[0], nextUnscaled);
            const fCurrScale = funcScaleInterp(0, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp);
            funcScalingFunction = funcGetScalingFunction(0, fStartBevelWidth, fEndBevelWidth);
			cumulScaledOffset += funcScalingFunction(nextUnscaled, fCurrScale, shape, iShapeVertex, 0, axisX, axisY, cumulScaledOffset, scaledShapePoint);
            fPrevScale = fCurrScale;
            scaledShapePoint.addToRef(path[0], startPoint);
		}

        if(DEBUG_VISUALIZE_AXES && iShapeVertex === 0)
        {
            debugXAxes.push([path[0].clone(), path[0].add(axisX.scale(DEBUG_AXES_SIZE))]);
            debugYAxes.push([path[0].clone(), path[0].add(axisY.scale(DEBUG_AXES_SIZE))]);
            debugZAxes.push([path[0].clone(), path[0].add(axisZ.scale(DEBUG_AXES_SIZE))]);
        }

        let ribbonPath = [];
        if(options.funcOverrideSpecificPoints)
            options.funcOverrideSpecificPoints(ribbonPath, iShapeVertex, 0, startPoint);
        ribbonPath.push(startPoint.clone());
        for(let iPathVertex = 0; iPathVertex < iNumPathPoints - 2; iPathVertex++)
        {
            path[iPathVertex + 2].subtractToRef(path[iPathVertex + 1], nextLine);

            nextAxisZ = nextLine.clone().normalize();
            nextAxisX = vecUp;
            nextAxisZ.subtractToRef(axisZ, bisector);
            nextAxisY = BABYLON.Vector3.Cross(nextAxisZ, nextAxisX);
            if(nextAxisY.length() < 1e-4)
            {
                // Our segment direction matches our up axis...
                nextAxisX = bisector.clone();
                nextAxisX.normalize();
                nextAxisY = BABYLON.Vector3.Cross(nextAxisZ, nextAxisX);
            }
            
            planeParallel = BABYLON.Vector3.Cross(nextAxisZ, axisZ);
            planeNormal = BABYLON.Vector3.Cross(planeParallel, bisector);

            if(DEBUG_PRINT_AXES_INFO && iShapeVertex == 0) { 
                CoreUtils.log(`Shape idx = ${iShapeVertex} path idx = ${iPathVertex} Next axes: X = ${nextAxisX.x}, ${nextAxisX.y}, ${nextAxisX.z}  Y = ${nextAxisY.x}, ${nextAxisY.y}, ${nextAxisY.z}`);
                CoreUtils.log(`\tNext Z = ${nextAxisZ.x}, ${nextAxisZ.y}, ${nextAxisZ.z}  prevZ = ${axisZ.x}, ${axisZ.y}, ${axisZ.z} planeNorm = ${planeNormal.x}, ${planeNormal.y}, ${planeNormal.z}`);
            }

            plane = BABYLON.Plane.FromPositionAndNormal(path[iPathVertex + 1], planeNormal);

            ray = new BABYLON.Ray(startPoint, axisZ);
            distance = ray.intersectsPlane(plane);
            if(distance === null && planeNormal.length() < 1e-5)
            {
                path[iPathVertex + 1].subtractToRef(path[iPathVertex], tempCurrDiff);
                distance = tempCurrDiff.length();
            }
            // if(distance === null)
            //     CoreUtils.log(`Extrude distance is null for path point ${iPathVertex + 1}, the result will be incorrect.`);
            startPoint.addToRef(axisZ.scale(distance), nextStartPoint);

            if(DEBUG_VISUALIZE_AXES && iShapeVertex === 0)
            {
                debugXAxes.push([path[iPathVertex + 1].clone(), path[iPathVertex + 1].add(nextAxisX.scale(DEBUG_AXES_SIZE))]);
                debugYAxes.push([path[iPathVertex + 1].clone(), path[iPathVertex + 1].add(nextAxisY.scale(DEBUG_AXES_SIZE))]);
                debugZAxes.push([path[iPathVertex + 1].clone(), path[iPathVertex + 1].add(nextAxisZ.scale(DEBUG_AXES_SIZE))]);
                debugPlaneNormals.push([path[iPathVertex + 1].clone(), path[iPathVertex + 1].add(planeNormal.scale(DEBUG_AXES_SIZE))]);
            }

            // We must scale the next start point around the path vertex by the current scaling amount
            if(bHavePerStepScale)
			{
                let fScaleInterp = 0.5;
                if(fTotalPathLength > 0)
                    fScaleInterp = pPathCumulDistances[iPathVertex + 1]/fTotalPathLength;
                const fCurrScale = funcScaleInterp(fScaleInterp, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp);

                nextStartPoint.subtractToRef(path[iPathVertex + 1], nextUnscaled);
                funcScalingFunction = funcGetScalingFunction(fScaleInterp, fStartBevelWidth, fEndBevelWidth);
				cumulScaledOffset += funcScalingFunction(nextUnscaled, fCurrScale/fPrevScale, shape, iShapeVertex, iPathVertex, nextAxisX, nextAxisY, cumulScaledOffset, scaledShapePoint);
                fPrevScale = fCurrScale;
                scaledShapePoint.addToRef(path[iPathVertex + 1], nextStartPoint);
			}

            // See if we have an override path for this
            if(options.funcOverrideSpecificPoints)
                options.funcOverrideSpecificPoints(ribbonPath, iShapeVertex, iPathVertex, nextStartPoint);

            ribbonPath.push(nextStartPoint.clone());

            // For 3D paths, check to see if we changed our plane. For now we just hardcode the cos(90)
            // as the limit, but this may be exposed as a parameter if necessary
            let upAxisDot = BABYLON.Vector3.Dot(nextAxisY, axisY);
            if(wasPath3D && options.ensureShapeAlwaysUp && Math.abs(upAxisDot) < 1e-2)
            {
                nextAxisX = vecUp;
                nextAxisY = BABYLON.Vector3.Cross(nextAxisZ, nextAxisX);

                // Go over every path vertex and compute the path of the current shape vertex
                nextStartPoint = path[iPathVertex + 1].add(nextAxisX.scale(shape[iShapeVertex].x)).add(nextAxisY.scale(shape[iShapeVertex].y));

                if(bHavePerStepScale)
			    {
                    let fScaleInterp = 0.5;
                    if(fTotalPathLength > 0)
                        fScaleInterp = pPathCumulDistances[iPathVertex + 1]/fTotalPathLength;
                    const fCurrScale = funcScaleInterp(fScaleInterp, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp);

                    nextStartPoint.subtractToRef(path[iPathVertex + 1], nextUnscaled);
                    funcScalingFunction = funcGetScalingFunction(fScaleInterp, fStartBevelWidth, fEndBevelWidth);
				    cumulScaledOffset += funcScalingFunction(nextUnscaled, fCurrScale/fPrevScale, shape, iShapeVertex, iPathVertex, nextAxisX, nextAxisY, cumulScaledOffset, scaledShapePoint);
                    fPrevScale = fCurrScale;
                    scaledShapePoint.addToRef(path[iPathVertex + 1], nextStartPoint);
			    }

                ribbonPath.push(nextStartPoint.clone());
            }

            axisX = nextAxisX.clone();
            axisY = nextAxisY.clone();
            axisZ = nextAxisZ.clone();
            startPoint = nextStartPoint.clone();
        }

        // Last point
        if(bDoClose)
        {
            path[0].subtractToRef(path[iNumPathPoints - 1], nextLine);
            
            nextAxisZ = nextLine.clone().normalize();
            nextAxisX = vecUp;
            nextAxisY = BABYLON.Vector3.Cross(nextAxisZ, nextAxisX);
            nextAxisZ.subtractToRef(axisZ, bisector);
            planeParallel = BABYLON.Vector3.Cross(nextAxisZ, axisZ);
            planeNormal = BABYLON.Vector3.Cross(planeParallel, bisector);
            plane = BABYLON.Plane.FromPositionAndNormal(path[iNumPathPoints - 1], planeNormal);
            ray = new BABYLON.Ray(startPoint, axisZ);
            distance = ray.intersectsPlane(plane);
            if(distance === null && planeNormal.length() < 1e-5)
                distance = nextLine.length();
            // if(distance === null)
            //     CoreUtils.log(`Extrude distance is null for path point ${iPathVertex + 1}, the result will be incorrect.`);
            startPoint.addToRef(axisZ.scale(distance), nextStartPoint);
            if(bHavePerStepScale)
			{
                nextStartPoint.subtractToRef(path[iNumPathPoints - 1], nextUnscaled);
                const fCurrScale = funcScaleInterp(1, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp);
				
                funcScalingFunction = funcGetScalingFunction(1, fStartBevelWidth, fEndBevelWidth);
                cumulScaledOffset += funcScalingFunction(nextUnscaled, fCurrScale/fPrevScale, shape, iShapeVertex, iNumPathPoints - 1, nextAxisX, nextAxisY, cumulScaledOffset, scaledShapePoint);
                fPrevScale = fCurrScale;
                scaledShapePoint.addToRef(path[iNumPathPoints - 1], nextStartPoint);
			}
            if(options.funcOverrideSpecificPoints)
                options.funcOverrideSpecificPoints(ribbonPath, iShapeVertex, iNumPathPoints - 1, nextStartPoint);
            ribbonPath.push(nextStartPoint.clone());
            axisX = nextAxisX.clone();
            axisY = nextAxisY.clone();
            axisZ = nextAxisZ.clone();
            startPoint = nextStartPoint.clone();
            path[1].subtractToRef(path[0], nextLine);

            nextAxisZ = nextLine.clone().normalize();
            nextAxisX = vecUp;
            nextAxisY = BABYLON.Vector3.Cross(nextAxisZ, nextAxisX);
            nextAxisZ.subtractToRef(axisZ, bisector);
            planeParallel = BABYLON.Vector3.Cross(nextAxisZ, axisZ);
            planeNormal = BABYLON.Vector3.Cross(planeParallel, bisector);
            plane = BABYLON.Plane.FromPositionAndNormal(path[0], planeNormal);
            ray = new BABYLON.Ray(startPoint, axisZ);
            distance = ray.intersectsPlane(plane);
            if(distance === null && planeNormal.length() < 1e-5)
                distance = nextLine.length();
            // if(distance === null)
            //     CoreUtils.log(`Extrude distance is null for last path point, the result will be incorrect.`);
            startPoint.addToRef(axisZ.scale(distance), nextStartPoint);
            if(bHavePerStepScale)
		    {
                nextStartPoint.subtractToRef(path[0], nextUnscaled);
                funcScalingFunction = funcGetScalingFunction(0, fStartBevelWidth, fEndBevelWidth);
				cumulScaledOffset += funcScalingFunction(nextUnscaled, fStartScale/fPrevScale, shape, iShapeVertex, iNumPathPoints, nextAxisX, nextAxisY, cumulScaledOffset, scaledShapePoint);
                scaledShapePoint.addToRef(path[0], nextStartPoint);
		    }
            ribbonPath.shift();
            if(options.funcOverrideSpecificPoints)
                options.funcOverrideSpecificPoints(ribbonPath, iShapeVertex, iNumPathPoints, nextStartPoint);
            ribbonPath.unshift(nextStartPoint.clone());
            ribbonPath.push(nextStartPoint.clone());
        } 
        else
        {
            planeNormal = axisZ;

            plane = BABYLON.Plane.FromPositionAndNormal(path[iNumPathPoints - 1], planeNormal);
            ray = new BABYLON.Ray(startPoint, axisZ);
            distance = ray.intersectsPlane(plane);
            // if(distance === null)
            //     CoreUtils.log(`Extrude distance is null for last path point , the result will be incorrect.`);
            startPoint.addToRef(axisZ.scale(distance), nextStartPoint);

            if(bHavePerStepScale)
			{
                nextStartPoint.subtractToRef(path[iNumPathPoints - 1], nextUnscaled);
                const fCurrScale = funcScaleInterp(1, fStartScale, fEndScale, fStartBevelWidth, fStartBevelScale, fEndBevelWidth, fEndBevelScale, funcStartBevelInterp, funcEndBevelInterp);
                funcScalingFunction = funcGetScalingFunction(1, fStartBevelWidth, fEndBevelWidth);
				cumulScaledOffset += funcScalingFunction(nextUnscaled, fCurrScale/fPrevScale, shape, iShapeVertex, iNumPathPoints - 1, axisX, axisY, cumulScaledOffset, scaledShapePoint);
                scaledShapePoint.addToRef(path[iNumPathPoints - 1], nextStartPoint);
			}

            if(options.funcOverrideSpecificPoints)
                options.funcOverrideSpecificPoints(ribbonPath, iShapeVertex, iNumPathPoints - 1, nextStartPoint);

            ribbonPath.push(nextStartPoint.clone());
        }

        allPaths.push(ribbonPath);
    }

    // Compute bbox and center
    let vecStartMin = new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    let vecStartMax = new BABYLON.Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
    let vecEndMin = new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    let vecEndMax = new BABYLON.Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
	allPaths.forEach((pointPath) =>
	{
        Utils3D.includePoint(pointPath[0], vecStartMin, vecStartMax);
        Utils3D.includePoint(pointPath[pointPath.length - 1], vecEndMin, vecEndMax);
	});

    let vecStartCenter = vecStartMax.add(vecStartMin).scale(0.5);

    if(DEBUG_VISUALIZE_AXES)
    {
        Utils3D.debugShowLines(debugXAxes, scene, BABYLON.Color3.Red());
        Utils3D.debugShowLines(debugYAxes, scene, BABYLON.Color3.Green());
        Utils3D.debugShowLines(debugZAxes, scene, BABYLON.Color3.Blue());
        Utils3D.debugShowLines(debugPlaneNormals, scene, BABYLON.Color3.Yellow());
    }

    // Compute UVs:   
    const { customUVs, shapeTextureCoords } = Extrude.computeCustomUVsForRibbon(shape, allPaths, bDoClose);

    let pMesh = BABYLON.MeshBuilder.CreateRibbon('ribbon', { pathArray: allPaths, sideOrientation: BABYLON.Mesh.FRONTSIDE, closeArray: true, closePath: bDoClose, uvs: customUVs }, scene);
    pMesh.name = name;
    return Extrude.capAndFinalizeMesh(scene, pMesh, allPaths, options, capType, path, bDoClose, origPathCenter.add(vecShapeAlignOffset3D), shapeTextureCoords, vecShapeCenter3D, vecPathStartPoint);
}
/*****************************************************************************/
static computeCustomUVsForRibbon(shape, allPaths, bDoClose)
{
    let nextLine = BABYLON.Vector3.Zero();

    // TODO: Shouldn't we compute these per shape level, since we might be scaling things?
    let cumulDist = 0;
    let currDist;
    let shapeTextureCoords = [0];
    for(let iShapeVertex = 1; iShapeVertex <= shape.length; iShapeVertex++)
	{
        shape[iShapeVertex % shape.length].subtractToRef(shape[iShapeVertex - 1], nextLine);
        currDist = nextLine.length()*UV_SCALE_MULT;
        cumulDist += currDist;
        shapeTextureCoords.push(cumulDist);
	}

    let customUVs = [];
    for(let iShapePointIndex = 0; iShapePointIndex < allPaths.length; iShapePointIndex++)
    {
        let pathCumulDist = 0;
        let iPathStep;
        for(iPathStep = 0; iPathStep < allPaths[iShapePointIndex].length; iPathStep++)    
        {
            if(iPathStep > 0)
            {
                allPaths[iShapePointIndex][iPathStep].subtractToRef(allPaths[iShapePointIndex][iPathStep - 1], nextLine);
                pathCumulDist += nextLine.length()*UV_SCALE_MULT;
            }
            
            let uvPoint = {
                x: shapeTextureCoords[iShapePointIndex], 
                y: pathCumulDist
            };
            customUVs.push(uvPoint);
        }

        // Babylon code adds one more vertex if the close flag is true
        if(bDoClose)
        {
            let iNumPaths = allPaths[iShapePointIndex].length;
            allPaths[iShapePointIndex][0].subtractToRef(allPaths[iShapePointIndex][iNumPaths - 1], nextLine);
            pathCumulDist += nextLine.length()*UV_SCALE_MULT;

            let uvPoint = {
                x: shapeTextureCoords[iShapePointIndex],
                y: pathCumulDist
            };
            customUVs.push(uvPoint);
        }
    }

    return { customUVs, shapeTextureCoords };
}
/*****************************************************************************/
static capAndFinalizeMesh(scene, pMesh, allPaths, options, capType, path,
                            bDoClose = true, origPathCenter = null, shapeTextureCoords = null, 
                            vecShapeCenter3D = null, vecPathStartPoint = null)
{
    const origName = pMesh.name;

    const smoothAngle = (options.smoothAngle === undefined) ? 0.0 : options.smoothAngle;

    const uOffset = options.uOffset ? options.uOffset : 0;
    const vOffset = options.vOffset ? options.vOffset : 0;
    const uvRotation = options.uvRotation ? options.uvRotation : 0;

    const capUVRotation = options.capUVRotation ? options.capUVRotation : uvRotation;
    const capUOffset = options.capUOffset ? options.capUOffset : uOffset;
    const capVOffset = options.capVOffset ? options.capVOffset : vOffset;

    const uvOffsetsInWorldSpace = options.uvOffsetsInWorldSpace !== undefined ? options.uvOffsetsInWorldSpace : true;

    // Now, create caps if necessary:
    let otherPolysToMerge = [];
    if(capType == BABYLON.Mesh.CAP_ALL || capType == BABYLON.Mesh.CAP_START)
	{
        let vecSideAxis = allPaths[1][0].subtract(allPaths[0][0]);
        vecSideAxis.normalize();
        let vecInOutAxis = allPaths[2][0].subtract(allPaths[0][0]);
        vecInOutAxis.normalize();
        let vecNormal = BABYLON.Vector3.Cross(vecSideAxis, vecInOutAxis);
        vecNormal.normalize();
        Utils3D.getCoordSystemFromNormal(vecNormal, vecInOutAxis, vecSideAxis);

        let vecCapShape = [];
        for(let iShapeVertex = 0; iShapeVertex < allPaths.length; iShapeVertex++)
		{
            let fX = BABYLON.Vector3.Dot(allPaths[iShapeVertex][0], vecSideAxis);
            let fY = BABYLON.Vector3.Dot(allPaths[iShapeVertex][0], vecInOutAxis);
            vecCapShape.push(new BABYLON.Vector3(fX, 0, fY));
		}

        let polygon = BABYLON.Mesh.CreatePolygon("ribbonCapStart", vecCapShape, scene);
        polygon.rotation.y = Math.PI/2.0;
        polygon.scaling.x *= -1.0;

        let trunkVerts = polygon.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        // We can have cases where the start and end of the shape are very close, and BJS
        // code will merge them. If we set full vertices here, the merge will error out
		// due to a mismatch between normals and positions.
        const iNumVerts = Math.min(Math.floor(trunkVerts.length / 3), allPaths.length);
        for(let iShapePointIndex = 0; iShapePointIndex < iNumVerts; iShapePointIndex++)
		{
            trunkVerts[iShapePointIndex*3] = allPaths[iShapePointIndex][0].z;
            trunkVerts[iShapePointIndex*3 + 1] = allPaths[iShapePointIndex][0].y;
            trunkVerts[iShapePointIndex*3 + 2] = allPaths[iShapePointIndex][0].x;
		}

        polygon.setVerticesData(BABYLON.VertexBuffer.PositionKind, trunkVerts, true);

        let fSignedArea = GeoUtils.calculateSignedPolygonAreaXZ(vecCapShape);
        if(fSignedArea <= 0)
            polygon.flipFaces(true);

        GeoUtils.applyBoxUVsToMeshes([polygon], -capUOffset, capVOffset, capUVRotation);
        if(polygon.getTotalIndices() > 0)
            otherPolysToMerge.push(polygon);
        else
            polygon.dispose();
	}

    if(capType == BABYLON.Mesh.CAP_ALL || capType == BABYLON.Mesh.CAP_END)
	{
        let iNumPathPoints = allPaths[0].length;

        let vecSideAxis = allPaths[1][iNumPathPoints - 1].subtract(allPaths[0][iNumPathPoints - 1]);
        vecSideAxis.normalize();
        let vecInOutAxis = allPaths[2][iNumPathPoints - 1].subtract(allPaths[0][iNumPathPoints - 1]);
        vecInOutAxis.normalize();
        let vecNormal = BABYLON.Vector3.Cross(vecSideAxis, vecInOutAxis);
        vecNormal.normalize();
        Utils3D.getCoordSystemFromNormal(vecNormal, vecInOutAxis, vecSideAxis);

        let vecCapShape = [];
        for(let iShapeVertex = 0; iShapeVertex < allPaths.length; iShapeVertex++)
        {
            let fX = BABYLON.Vector3.Dot(allPaths[iShapeVertex][iNumPathPoints - 1], vecSideAxis);
            let fY = BABYLON.Vector3.Dot(allPaths[iShapeVertex][iNumPathPoints - 1], vecInOutAxis);
            vecCapShape.push(new BABYLON.Vector3(fX, 0, fY));
        }

        let polygon = BABYLON.Mesh.CreatePolygon("ribbonCapEnd", vecCapShape, scene);
        polygon.rotation.y = Math.PI/2.0;
        polygon.scaling.x *= -1.0;

        let trunkVerts = polygon.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        // We can have cases where the start and end of the shape are very close, and BJS
        // code will merge them. If we set full vertices here, the merge will error out
		// due to a mismatch between normals and positions.
		const iNumVerts = Math.min(Math.floor(trunkVerts.length / 3), allPaths.length);
        for(let iShapePointIndex = 0; iShapePointIndex < iNumVerts; iShapePointIndex++)
		{
            trunkVerts[iShapePointIndex*3] = allPaths[iShapePointIndex][iNumPathPoints - 1].z;
            trunkVerts[iShapePointIndex*3 + 1] = allPaths[iShapePointIndex][iNumPathPoints - 1].y;
            trunkVerts[iShapePointIndex*3 + 2] = allPaths[iShapePointIndex][iNumPathPoints - 1].x;
		}

        polygon.setVerticesData(BABYLON.VertexBuffer.PositionKind, trunkVerts, true);

        let fSignedArea = GeoUtils.calculateSignedPolygonAreaXZ(vecCapShape);
        if(fSignedArea > 0)
            polygon.flipFaces(true);

        GeoUtils.applyBoxUVsToMeshes([polygon], -capUOffset, -capVOffset, -capUVRotation);
        if(polygon.getTotalIndices() > 0)
        otherPolysToMerge.push(polygon);
        else
            polygon.dispose();
	}

    if(options.flatShaded)
	{
        let iStartCapCount = 0;
        let iEndCapCount = 0;

        let vecLocalPointPosition = BABYLON.Vector3.Zero();
        let vecGlobalPointPosition = BABYLON.Vector3.Zero();
        let vecToPathSegmentStart = BABYLON.Vector3.Zero();

        const iClosePathCorr = (bDoClose) ? 1 : 0;
        // If we have caps, we'll apply smoothing later, after merging
        if(otherPolysToMerge.length > 0)
            pMesh.convertToFlatShadedMesh();
        else
            GeoUtils.recomputeNormals(pMesh, smoothAngle);

		pMesh.computeWorldMatrix(true);
		let currWorldMatrix = pMesh.getWorldMatrix();

        let pathCopyWithCapPoints = path.slice();
        if(iStartCapCount > 0 && pathCopyWithCapPoints.length > 0)
            pathCopyWithCapPoints.unshift(pathCopyWithCapPoints[0].clone());

        if(iEndCapCount > 0 && pathCopyWithCapPoints.length > 0)
            pathCopyWithCapPoints.push(pathCopyWithCapPoints[pathCopyWithCapPoints.length - 1].clone());

        if(iClosePathCorr > 0 && pathCopyWithCapPoints.length > 0)
            pathCopyWithCapPoints.push(pathCopyWithCapPoints[0].clone());

        let nextLine = BABYLON.Vector3.Zero();
        let line = BABYLON.Vector3.Zero();
        let cumulDist = 0;
        let cumulDistancesPerPathSegment = [0];
        for(let iPathVertex = 1; iPathVertex < pathCopyWithCapPoints.length; iPathVertex++)
        {
            pathCopyWithCapPoints[iPathVertex].subtractToRef(pathCopyWithCapPoints[iPathVertex - 1], line);
            cumulDist += line.length();
            cumulDistancesPerPathSegment[iPathVertex] = cumulDist;
        }

        // Apply new UVs
        let uvs = pMesh.getVerticesData(BABYLON.VertexBuffer.UVKind);
		let positions = pMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
		let indices = pMesh.getIndices();
        let fSegmentInterp;
		for(let index = 0; index < indices.length; index += 3)
		{
            // The structure this produces is two triangles per side of the shape (including caps) until all sides are exhausted
            // The iClosePathCorr*2 is because if we close the extrusion path, BJS includes a degenrate polygon at the end.
            // -1 is because we need to know the number of path line segments, not the number of vertices.
            const iNumPathLineSegments = (path.length + iClosePathCorr*2 + iEndCapCount + iStartCapCount) - 1;
            const iFaceIndex = index/3;
            let iPathSegmentIndexInclCaps = Math.floor((iFaceIndex % (iNumPathLineSegments*2))/2) + 1;

            // BJS includes a degenerate polygon at the end in case the close flag is true. Skip it.
            if(iClosePathCorr > 0 && iPathSegmentIndexInclCaps === iNumPathLineSegments)
               continue;

            let iSideIndex = Math.floor(iFaceIndex/(iNumPathLineSegments*2));
            
            pathCopyWithCapPoints[iPathSegmentIndexInclCaps % pathCopyWithCapPoints.length].subtractToRef(pathCopyWithCapPoints[iPathSegmentIndexInclCaps - 1], nextLine);
            let fLength = nextLine.length();
            nextLine.normalize();

			for(let subIndex = 0; subIndex < 3; subIndex++)
			{
				let pointIndex = indices[index + subIndex];
                vecLocalPointPosition.copyFromFloats(positions[pointIndex*3], positions[pointIndex*3 + 1], positions[pointIndex*3 + 2])				
                BABYLON.Vector3.TransformCoordinatesToRef(vecLocalPointPosition, currWorldMatrix, vecGlobalPointPosition);

                // Project it onto the appropriate path segment
                vecGlobalPointPosition.subtractToRef(pathCopyWithCapPoints[iPathSegmentIndexInclCaps - 1], vecToPathSegmentStart);
                let fCurrDist = BABYLON.Vector3.Dot(nextLine, vecToPathSegmentStart);
                if(fLength > 0.0)
                    fSegmentInterp = fCurrDist/fLength;
                else
                    fSegmentInterp = 0.0;
                let fVCoordinate = (1.0 - fSegmentInterp)*cumulDistancesPerPathSegment[iPathSegmentIndexInclCaps - 1] + fSegmentInterp*cumulDistancesPerPathSegment[iPathSegmentIndexInclCaps];
                fVCoordinate *= UV_SCALE_MULT;
                uvs[pointIndex*2 + 1] = fVCoordinate;

                // This is to take care of the last segment that will span the U coordinate between the last value
                // and 0 at the start. We need to set it to the proper computed distance, otherwise we may have
                // some arbitrary large value tiling back to 0.
                if(iSideIndex === (allPaths.length - 1) && uvs[pointIndex*2 + 0] === 0.0 && shapeTextureCoords)
                    uvs[pointIndex*2 + 0] = shapeTextureCoords[shapeTextureCoords.length - 1];
			}
        }

        pMesh.setVerticesData(BABYLON.VertexBuffer.UVKind, uvs, false, false);
	}

    if(uOffset != 0 || vOffset != 0 || uvRotation != 0)
    {
		let uvs = pMesh.getVerticesData(BABYLON.VertexBuffer.UVKind);
		let indices = pMesh.getIndices();
		let uvPoint = { x: null, y: null };
		let uvRotatedPoint = BABYLON.Vector3.Zero();

		for(let index = 0; index < indices.length; index++)
		{
			const pointIndex = indices[index];
			if(!uvOffsetsInWorldSpace || uvRotation !== 0)
			{
				uvPoint.x = uvs[pointIndex * 2 + 0];
				uvPoint.y = uvs[pointIndex * 2 + 1];
				if(!uvOffsetsInWorldSpace)
				{
					uvPoint.x -= uOffset;
					uvPoint.y -= vOffset;
				}
				Utils2D.rotatePointToRef(uvPoint, uvRotation, uvRotatedPoint);
				uvs[pointIndex * 2 + 0] = uvRotatedPoint.x;
				uvs[pointIndex * 2 + 1] = uvRotatedPoint.y;
			}

			if(uvOffsetsInWorldSpace || uvRotation === 0)
			{
				uvs[pointIndex * 2 + 0] -= uOffset;
				uvs[pointIndex * 2 + 1] -= vOffset;
			}
		}
		pMesh.setVerticesData(BABYLON.VertexBuffer.UVKind, uvs, false, false);
    }

    if(otherPolysToMerge.length > 0)
	{
        otherPolysToMerge.push(pMesh);
        pMesh = BABYLON.Mesh.MergeMeshes(otherPolysToMerge, true, true);
        // The above call can create zero-area, invalid triangles when
        // dealing with collinear vertices, and they will break *everything*
        // down the line, including room booleans. Remove them before they
        // cause any trouble.
        GeoUtils.removeInvalidTriangles(pMesh);
        GeoUtils.recomputeNormals(pMesh, smoothAngle);
	}

    pMesh.name = origName;    
	let bbox = pMesh.getBoundingInfo().boundingBox;
	let vecCenter = bbox.maximumWorld.add(bbox.minimumWorld).scale(-0.5);
	pMesh.position = vecCenter.clone();
    pMesh.bakeCurrentTransformIntoVertices();
    pMesh.position = vecCenter.scale(-1);

    if(!options.funcOverrideSpecificPoints)
    {
	    if(options.finalMeshAlign === 'toOrigin' || options.finalMeshAlign === 'toOriginBottomAtZero')
	    {
		    let vecSize = bbox.maximumWorld.subtract(bbox.minimumWorld);
		    if(options.finalMeshAlign === 'toOriginBottomAtZero')
			    pMesh.position = new BABYLON.Vector3(0, vecSize.y/2.0, 0);
		    else
			    pMesh.position = new BABYLON.Vector3(0, 0, 0);
	    }
        else if(options.finalMeshAlign === 'toPath' && vecPathStartPoint)
            pMesh.position.addInPlace(vecPathStartPoint);
        else if(options.finalMeshAlign === 'toPathCenter' && origPathCenter)
            pMesh.position = origPathCenter.clone();
        else if(options.finalMeshAlign === 'toShape' && vecShapeCenter3D)
            pMesh.position.addInPlace(vecShapeCenter3D);
    }

    return pMesh;
}
/*****************************************************************************/
}

const SCALING_FUNCTIONS = {
    'scale': Extrude.simpleScale,
    'offset': Extrude.smartScale,
};
